import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, DollarSign, CalendarDays, Eye, EyeOff, Mail, MapPin, PencilRuler, PenTool, Phone, Sun, User, Zap } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { useOrg, useOrgId } from "../api/org";
import { PanelCanvas } from "../components/PanelCanvas";
import { energySummary, perKwh } from "../components/PriceFields";
import { DesignProposalDownload } from "../components/ProposalActions";
import { EsignStatusLine, SendProposalButton } from "../components/SendProposal";
import { EmailProblemBadge } from "../components/LeadActivity";
import { ImportedDesignWithAi, ImportedSummary } from "../components/ImportedProject";
import { QuoteOfferCard } from "../components/QuoteOfferCard";
import { SeeReportButton } from "../components/ReportModal";
import { Alert, Badge, Button, Card, Empty, PageHead, Select, Spinner, useToast } from "../components/ui";
import { PermitSummary } from "../permits/PermitSummary";
import { date, money, num, shownVersion, titleCase } from "../lib/format";
import { otherRegionCurrency } from "../lib/currency";
import { DESIGN_STATUS_LABELS, leadName, leadState, reportPending } from "./Designs";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import type { MessageKey } from "../i18n/keys";
import type { Tr } from "../lib/tr";
import { sitePictureUrl, useDarkTheme } from "../lib/theme";

function useSiteImage(url: string | undefined) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!url) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    rawFetch(url)
      .then((r) => r.blob())
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => !cancelled && setImage(img);
        img.src = objectUrl;
      })
      .catch((e) => setError(errorMessage(e)));
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);
  return { image, error };
}

function Metric({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: React.ReactNode; sub?: React.ReactNode }) {
  return (
    <div className="card stat">
      <div className="label">
        {icon} <span style={{ minWidth: 0, overflowWrap: "anywhere", hyphens: "auto" }}>{label}</span>
      </div>
      <div className="value">{value}</div>
      {sub && <div className="sub">{sub}</div>}
    </div>
  );
}

const RATE_SOURCES: Record<string, MessageKey> = {
  company_utility: "designs.rate.companyUtility",
  company_global: "designs.rate.companyGlobal",
  solar_os: "designs.rate.solarOs",
  regional: "designs.rate.regional",
};

const FINANCING: Record<string, MessageKey> = {
  cash: "designs.financing.cash",
  loan: "designs.financing.loan",
  lease: "designs.financing.lease",
  ppa: "designs.financing.ppa",
};

const ORIGINS: Record<string, MessageKey> = {
  created: "designs.origin.version.created",
  auto_pipeline: "designs.origin.version.autoPipeline",
  manual_edit: "designs.origin.version.manualEdit",
  duplicate: "designs.origin.version.duplicate",
  import: "designs.origin.version.import",
  restore: "designs.origin.version.restore",
  sketch: "designs.origin.version.sketch",
};

const STAGES: Record<string, MessageKey> = {
  imagery: "designs.stageName.imagery",
  roof_outline: "designs.stageName.roofOutline",
  roof_model: "designs.stageName.roofModel",
  obstructions: "designs.stageName.obstructions",
  setbacks: "designs.stageName.setbacks",
  irradiance: "designs.stageName.irradiance",
  layout: "designs.stageName.layout",
  quote: "designs.stageName.quote",
};

const STAGE_STATES: Record<string, MessageKey> = {
  pending: "designs.stageState.pending",
  auto: "designs.stageState.auto",
  manual: "designs.stageState.manual",
  stale: "designs.stageState.stale",
  failed: "designs.stageState.failed",
};

/** A key from `map`, else the API's value made readable. */
function label(t: Tr, map: Record<string, MessageKey>, value: string): string {
  return map[value] ? t(map[value]!) : titleCase(value);
}

/** The rate the quote used, as it kept it (later price changes never alter it). */
function RateUsed({ tariff }: { tariff: S["Tariff"] }) {
  const t = useT();
  const rate = tariff.energy.type === "flat" && tariff.energy.rate != null ? `${perKwh(tariff.energy.rate, tariff.currency)}/kWh` : energySummary(tariff);
  const when = tariff.priced_on ? `${tariff.priced_on}T12:00:00` : tariff.retrieved_at ?? null; // a date: noon keeps the day in every zone
  const details = [tariff.utility, when ? date(when) : null].filter(Boolean).join(", ");
  return (
    <div className="rate-used" title={tariff.name}>
      <Zap size={14} />
      <span>
        {t("designs.rate.used")} <b>{rate}</b>
        {details ? `, ${details}` : ""}
      </span>
      {tariff.rate_source && RATE_SOURCES[tariff.rate_source] && (
        <Badge tone={tariff.rate_source.startsWith("company") ? "ok" : "info"}>{t(RATE_SOURCES[tariff.rate_source]!)}</Badge>
      )}
    </div>
  );
}

export function DesignDetail() {
  const t = useT();
  const { designId = "" } = useParams();
  // `?lead=<public quote id>`: the lead emails' "Open in dashboard" names the homeowner's quote (several visitors can
  // share one site's design).
  const [search] = useSearchParams();
  const leadParam = search.get("lead");
  const orgId = useOrgId();
  const canEdit = useOrg().can("designs_leads", "edit");
  const qc = useQueryClient();
  const toast = useToast();
  const [showAvailable, setShowAvailable] = useState(true);

  const design = useQuery({
    queryKey: ["design", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}", { params: { path: { design_id: designId } } })),
    // The roof report is drawn in the background after a design: show its button as soon as it is ready.
    refetchInterval: (q) => (q.state.data && reportPending(q.state.data.report, q.state.data.updated_at) ? 8000 : false),
  });
  const project = useQuery({
    queryKey: ["project", design.data?.project_id],
    enabled: !!design.data,
    queryFn: () => unwrap(api.GET("/v1/projects/{project_id}", { params: { path: { project_id: design.data!.project_id } } })),
  });
  const version = design.data?.current_version_number ?? null;
  // An imported project's design is its empty version 1 until "Design with AI": nothing to draw yet.
  const importedEmpty = !!project.data?.imported_from && version === 1;
  const site = useQuery({
    queryKey: ["site", designId, version],
    enabled: !!version && project.isSuccess && !importedEmpty,
    retry: false,
    queryFn: () =>
      unwrap(
        api.GET("/v1/designs/{design_id}/versions/{version_number}/site", {
          params: { path: { design_id: designId, version_number: version! } },
        }),
      ),
  });
  const quotes = useQuery({
    queryKey: ["quotes", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/quotes", { params: { path: { design_id: designId }, query: { limit: 1 } } })),
  });
  const versionDoc = useQuery({
    queryKey: ["version", designId, version],
    enabled: !!version,
    queryFn: () =>
      unwrap(
        api.GET("/v1/designs/{design_id}/versions/{version_number}", {
          params: { path: { design_id: designId, version_number: version! } },
        }),
      ),
  });
  // No saved quote (a Designer design has no website quote): the price is the current version's quote stage.
  const quoteStage = versionDoc.data?.stages.find((s) => s.stage === "quote");
  const stageQuote = useQuery({
    queryKey: ["stage-quote", designId, version],
    enabled: !!version && quotes.isSuccess && quotes.data.items.length === 0 && !!quoteStage?.output_id && (quoteStage.state === "auto" || quoteStage.state === "manual"),
    queryFn: () =>
      unwrap(
        api.GET("/v1/designs/{design_id}/versions/{version_number}/stages/{stage}", {
          params: { path: { design_id: designId, version_number: version!, stage: "quote" } },
        }),
      ),
  });
  // The lead (website quote) of this design, from the leads list.
  const lead = useQuery({
    queryKey: ["lead-of", orgId, designId],
    queryFn: async () => {
      const page = await unwrap(api.GET("/v1/orgs/{org_id}/leads", { params: { path: { org_id: orgId }, query: { limit: 100 } } }));
      return page.items.find((i) => i.design_id === designId) ?? null;
    },
  });
  const updateStatus = useMutation({
    mutationFn: (status: S["DesignStatus"]) =>
      unwrap(api.PATCH("/v1/designs/{design_id}", { params: { path: { design_id: designId } }, body: { status } })),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["design", designId] });
      qc.invalidateQueries({ queryKey: ["leads-infinite"] });
      toast(t("designs.detail.statusUpdated"));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const dark = useDarkTheme();
  const siteUrl = site.data?.site_image?.url;
  const { image, error: imageError } = useSiteImage(siteUrl ? sitePictureUrl(siteUrl, { sketch: design.data?.mode === "sketch", dark }) : undefined);

  if (design.isLoading) return <Spinner />;
  if (design.error || !design.data) return <Alert tone="danger">{errorMessage(design.error)}</Alert>;

  const d = design.data;
  const quote = quotes.data?.items[0]?.output ?? (stageQuote.data?.payload as S["QuoteOutput"] | undefined);
  const summary = quote?.system_summary;
  const fin = quote?.financials;
  const currency = quote?.currency ?? "USD";
  // a saved quote priced for another region (another currency than the house is priced in today): shown as that saved
  // quote, never as the design's price; the editor, Savings and Options price the version again (QA round 4)
  const otherRegion = otherRegionCurrency(quote, d.pricing_currency);
  const layoutSummary = versionDoc.data?.stages.find((s) => s.stage === "layout")?.summary;
  const kw = summary?.system_size_kw ?? layoutSummary?.kw_dc ?? lead.data?.kw_dc;
  const panels = summary?.selected_panel_count ?? layoutSummary?.module_count ?? lead.data?.panel_count;
  const production = summary?.est_annual_production_kwh ?? layoutSummary?.annual_kwh;
  const address = lead.data?.address || project.data?.address || project.data?.normalized_address || d.name;
  const state = lead.data ? leadState(lead.data, t) : null;
  const contact = lead.data?.contact;

  return (
    <>
      <PageHead
        crumbs={
          <>
            <Link to="/designs">
              <DirIcon><ArrowLeft size={12} style={{ verticalAlign: -1 }} /></DirIcon> {t("shell.nav.designsLeads")}
            </Link>
          </>
        }
        title={<span dir="auto">{address}</span>}
        description={
          <span className="row" style={{ gap: 8 }}>
            {state && <Badge tone={state.tone}>{state.label}</Badge>}
            {d.mode === "sketch" && <Badge tone="info" plain><PenTool size={11} /> Sketch</Badge>}
            <span>
              {version
                ? t("designs.detail.meta", { name: d.name, version: shownVersion(version, d.version_offset), created: date(d.created_at, true) })
                : t("designs.detail.metaNoVersion", { name: d.name, created: date(d.created_at, true) })}
            </span>
          </span>
        }
        actions={
          <div className="row">
            <SeeReportButton designId={designId} title={address} available={d.report?.available} versionOffset={d.version_offset} size="lg" />
            {d.mode === "sketch" && (
              <Link to={`/designs/${designId}/sketch`} className={`btn ${version && version > 2 ? "" : "btn-primary"}`}>
                <PenTool size={16} /> {t("designs.detail.openSketch")}
              </Link>
            )}
            {version && version > (d.mode === "sketch" ? 2 : 1) && (
              <Link to={`/designs/${designId}/edit`} className="btn btn-primary">
                <PencilRuler size={16} /> {t("designs.detail.openEditor")}
              </Link>
            )}
            <div style={{ width: 160 }}>
              <Select
                value={d.status}
                disabled={!canEdit}
                onChange={(s) => updateStatus.mutate(s)}
                ariaLabel={t("designs.filter.status")}
                options={(["draft", "proposed", "sold", "archived"] as const).map((s) => ({ value: s, label: t("designs.detail.statusOption", { status: t(DESIGN_STATUS_LABELS[s]!) }) }))}
              />
            </div>
          </div>
        }
      />
      <div className="split-wide">
        <div className="stack">
          <Card pad={false}>
            {site.isLoading || (site.data?.site_image && !image && !imageError) ? (
              <div className="design-canvas skeleton" />
            ) : site.data?.site_image && image ? (
              <div className="design-canvas">
                <PanelCanvas site={site.data} image={image} showAvailable={showAvailable} />
                <div className="overlay-tools">
                  <Button size="sm" icon={showAvailable ? <EyeOff /> : <Eye />} onClick={() => setShowAvailable((v) => !v)}>
                    {showAvailable ? t("designs.detail.hidePositions") : t("designs.detail.showPositions")}
                  </Button>
                </div>
              </div>
            ) : importedEmpty ? (
              <div className="card-body">
                <ImportedDesignWithAi orgId={orgId} designId={designId} />
              </div>
            ) : d.mode === "sketch" && !site.error && !imageError ? (
              <Empty icon={<PenTool />} title={t("designs.detail.nothingDrawn")}>
                {t("designs.detail.nothingDrawnBody")}
              </Empty>
            ) : (
              <Empty icon={<Sun />} title={t("designs.detail.noRoof")}>
                {imageError || (site.error ? errorMessage(site.error) : t("designs.detail.noRoofBody"))}
              </Empty>
            )}
          </Card>
          <div className="grid-4 stats-tight">
            <Metric icon={<Zap size={14} />} label={t("designs.metric.systemSize")} value={<>{num(kw, 1)}<small>kW</small></>} sub={summary ? t("designs.metric.panelWatts", { watts: num(summary.panel_watts) }) : undefined} />
            <Metric
              icon={<Sun size={14} />}
              label={t("designs.metric.panels")}
              value={num(panels)}
              sub={summary ? t("designs.metric.fit", { min: num(summary.min_panels), max: num(summary.max_panels) }) : site.data ? t("designs.metric.positions", { count: site.data.panels.length }) : undefined}
            />
            <Metric
              icon={<CalendarDays size={14} />}
              label={t("designs.metric.production")}
              value={<>{num(production)}<small>{t("designs.metric.kwhPerYear")}</small></>}
              sub={summary ? t("designs.metric.offset", { percent: summary.est_offset_percentage / 100 }) : t("designs.metric.dcBeforeLosses")}
            />
            <Metric icon={<DollarSign size={14} />} label={t("designs.metric.price")} value={fin ? money(fin.full_system_price, currency) : "—"} sub={otherRegion ? t("designs.metric.otherRegion", { currency: otherRegion }) : fin ? t("designs.metric.afterIncentives", { amount: money(fin.net_cost, currency) }) : t("designs.metric.notPriced")} />
          </div>
        </div>
        <div className="stack">
          <Card title={t("designs.column.lead")}>
            {contact ? (
              <div className="stack" style={{ gap: 10 }}>
                <div className="row">
                  <User size={16} className="faint" /> <b>{leadName(lead.data!) ?? "—"}</b>
                </div>
                {contact.email && (
                  <div className="row">
                    <Mail size={16} className="faint" /> <a href={`mailto:${contact.email}`} dir="auto">{contact.email}</a>
                    {(leadParam || lead.data?.public_quote_id) && <EmailProblemBadge orgId={orgId} publicQuoteId={(leadParam || lead.data?.public_quote_id)!} />}
                  </div>
                )}
                {contact.phone && (
                  <div className="row">
                    <Phone size={16} className="faint" /> <a href={`tel:${contact.phone}`} dir="ltr">{contact.phone}</a>
                  </div>
                )}
                <div className="row">
                  <MapPin size={16} className="faint" /> <span className="muted" dir="auto">{address}</span>
                </div>
                {lead.data?.roof_type && <div className="small muted">{t("designs.detail.roofType", { type: titleCase(lead.data.roof_type.replace(/-/g, " ")) })}</div>}
              </div>
            ) : (
              <p className="muted">
                {t(lead.data?.source === "website" ? "designs.detail.noContactWebsite" : lead.data?.source === "import" ? "designs.detail.noContactImport" : "designs.detail.noContactDashboard")}
              </p>
            )}
          </Card>
          {project.data?.imported_from && <ImportedSummary orgId={orgId} project={project.data} lead={lead.data} />}
          {leadParam || (lead.data?.public_quote_id && lead.data.public_status === "completed") ? (
            <QuoteOfferCard orgId={orgId} publicQuoteId={(leadParam || lead.data?.public_quote_id)!} homeownerName={lead.data ? leadName(lead.data) : null} />
          ) : (
            // the design's own proposal card only once we know it has no lead: shown while the lead was still loading,
            // it flashed and asked for the design's signature requests before the lead's card replaced it
            fin && !lead.isPending && (
              <Card title={t("designs.detail.proposal")} description={t("designs.detail.proposalDescription")}>
                <div className="stack" style={{ gap: 10 }}>
                  <div className="row">
                    <DesignProposalDownload designId={designId} />
                    <SendProposalButton target={{ orgId, designId }} />
                  </div>
                  <EsignStatusLine target={{ orgId, designId }} />
                  <p className="small muted" style={{ margin: 0 }}>
                    {t("designs.detail.proposalNote")}
                  </p>
                </div>
              </Card>
            )
          )}
          {layoutSummary && version && (
            <Card title={t("designs.detail.planSet")} description={t("designs.detail.planSetDescription")}>
              <PermitSummary designId={designId} version={version} offset={d.version_offset} />
            </Card>
          )}
          <Card title={t("designs.detail.quote")} description={quote ? t("designs.detail.quoteAsOf", { date: date(quote.as_of), financing: label(t, FINANCING, quote.financing) }) : undefined}>
            {otherRegion && fin && (
              <div style={{ marginBottom: 12 }}>
                <Alert tone="warn">{t("designs.detail.quoteOtherRegion", { currency: otherRegion })}</Alert>
              </div>
            )}
            {fin && summary ? (
              <dl className="kv">
                <dt>{t("designs.quote.systemPrice")}</dt>
                <dd>{money(fin.full_system_price, currency)}</dd>
                <dt>{t("designs.quote.incentives")}</dt>
                <dd>{t("designs.quote.minus", { amount: money(fin.incentives, currency) })}</dd>
                <dt>{t("designs.quote.netCost")}</dt>
                <dd>{money(fin.net_cost, currency)}</dd>
                <dt>{t("designs.quote.pricePerWatt")}</dt>
                <dd>{money(fin.price_per_watt, currency, 2)}</dd>
                <dt>{t("designs.quote.bill")}</dt>
                <dd>{t("designs.quote.billValues", { before: money(fin.monthly_bill_old, currency), after: money(fin.monthly_bill_new, currency) })}</dd>
                <dt>{t("designs.quote.savingsYear1")}</dt>
                <dd>{money(fin.annual_savings_year1, currency)}</dd>
                <dt>{t("designs.quote.payback")}</dt>
                <dd>{fin.payback_years != null ? t("designs.quote.years", { years: num(fin.payback_years, 1), count: fin.payback_years }) : "—"}</dd>
                <dt>{t("designs.quote.lifetimeSavings")}</dt>
                <dd>{money(fin.lifetime_savings, currency)}</dd>
                <dt>{t("designs.quote.equipment")}</dt>
                <dd className="ellipsis" title={summary.panel_model}>
                  {summary.selected_panel_count} × {summary.panel_model}
                </dd>
                <dt>{t("designs.quote.inverter")}</dt>
                <dd className="ellipsis">
                  {summary.inverter_count} × {summary.inverter_model}
                </dd>
              </dl>
            ) : (
              <p className="muted">
                {lead.data?.source === "website" ? t("designs.quote.noneWebsite") : t("designs.quote.noneDashboard")}
              </p>
            )}
            {fin && summary && quote?.tariff && <RateUsed tariff={quote.tariff} />}
          </Card>
          {versionDoc.data && (
            <Card
              title={t("designs.detail.pipeline")}
              description={t("designs.detail.pipelineVersion", { version: shownVersion(versionDoc.data.number, d.version_offset), origin: label(t, ORIGINS, versionDoc.data.origin) })}
            >
              <div className="pipeline-flow">
                {versionDoc.data.stages.map((s) => (
                  <span key={s.stage} className={`node${s.state === "pending" ? " off" : ""}`} title={label(t, STAGE_STATES, s.state)}>
                    {label(t, STAGES, s.stage)}
                    {s.state === "failed" ? " ✕" : s.state === "manual" ? " ✎" : ""}
                  </span>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}
